import styled from '@/styles';

// Layout shells used across the v2 order creation flow. Centralised here
// so the steps/modals/components don't reach for inline `style={{...}}`.

export const WizardContainer = styled('div', {
  padding: 24,
  maxWidth: 960,
  margin: '0 auto',
});

export const WizardShell = styled('div', {
  position: 'fixed',
  inset: 0,
  zIndex: 3100,
  display: 'flex',
  flexDirection: 'column',
  background: '#FCFCFC',
  overflow: 'hidden',
});

export const WizardBody = styled('div', {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
});

export const StickyFooter = styled('div', {
  position: 'sticky',
  bottom: 0,
  background: '#FCFCFC',
  borderTop: '1px solid #EAEAEA',
  padding: '12px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  flexShrink: 0,
  zIndex: 10,
});

export const FooterCancel = styled('button', {
  height: 32,
  width: 100,
  border: '1px solid #44A735',
  borderRadius: 8,
  background: '#fff',
  color: '#2E8722',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "'Manrope', sans-serif",
  cursor: 'pointer',
  '&:hover': { background: '#F5FFF4' },
});

export const FooterPrimary = styled('button', {
  height: 32,
  minWidth: 100,
  padding: '0 14px',
  border: 'none',
  borderRadius: 8,
  background: '#44A735',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  fontFamily: "'Manrope', sans-serif",
  cursor: 'pointer',
  boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
  '&:hover': { background: '#2E8722' },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    background: '#9DC97A',
  },
});

export const FooterGhost = styled('button', {
  height: 32,
  width: 100,
  border: '1px solid #DADADA',
  borderRadius: 8,
  background: '#fff',
  color: '#5B5B5B',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "'Manrope', sans-serif",
  cursor: 'pointer',
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
});

export const Section = styled('section', {
  marginBottom: 24,
});

export const SectionHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const Row = styled('div', {
  display: 'flex',
  gap: 8,
  variants: {
    gap: {
      sm: { gap: 6 },
      md: { gap: 8 },
      lg: { gap: 12 },
      xl: { gap: 16 },
    },
    align: {
      center: { alignItems: 'center' },
      start: { alignItems: 'flex-start' },
      end: { alignItems: 'flex-end' },
      baseline: { alignItems: 'baseline' },
    },
    justify: {
      between: { justifyContent: 'space-between' },
      end: { justifyContent: 'flex-end' },
      center: { justifyContent: 'center' },
    },
    wrap: {
      true: { flexWrap: 'wrap' },
    },
  },
});

export const Column = styled('div', {
  display: 'grid',
  gap: 8,
  variants: {
    gap: {
      sm: { gap: 6 },
      md: { gap: 8 },
      lg: { gap: 12 },
      xl: { gap: 16 },
    },
  },
});

export const Card = styled('div', {
  border: '1px solid $colors$neutralLightest',
  borderRadius: 12,
  padding: 16,
});

export const ModalShell = styled('div', {
  border: '1px solid $colors$neutralLightest',
  borderRadius: 12,
  padding: 16,
  marginTop: 12,
});

export const FieldLabel = styled('label', {
  display: 'block',
  marginBottom: 4,
});

export const MutedText = styled('p', {
  color: '$colors$textLighter',
  margin: 0,
});

export const ErrorText = styled('div', {
  color: '$colors$dangerDefault',
});

export const ButtonRow = styled('div', {
  display: 'flex',
  gap: 8,
  marginTop: 12,
  variants: {
    align: {
      between: { justifyContent: 'space-between' },
      end: { justifyContent: 'flex-end' },
    },
    spacing: {
      tight: { marginTop: 6 },
      cozy: { marginTop: 8 },
      loose: { marginTop: 16 },
    },
  },
});

export const Pill = styled('button', {
  borderRadius: 999,
  padding: '6px 12px',
  border: '1px solid $colors$neutralLightest',
  background: 'transparent',
  cursor: 'pointer',
  variants: {
    active: {
      true: {
        borderColor: '$colors$textDefault',
        borderWidth: 2,
      },
    },
  },
});

export const SelectableTile = styled('button', {
  textAlign: 'left',
  width: '100%',
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid $colors$neutralLightest',
  background: '$colors$neutralGhost',
  cursor: 'pointer',
  variants: {
    active: {
      true: {
        border: '2px solid $colors$textDefault',
      },
    },
  },
});

export const TileTitle = styled('div', {
  fontWeight: 500,
});

export const TileSubtitle = styled('div', {
  color: '$colors$textLighter',
  fontSize: 12,
});

export const FormGrid = styled('div', {
  display: 'grid',
  gap: 8,
  variants: {
    columns: {
      address: { gridTemplateColumns: '2fr 1fr 1fr' },
      payRate: { maxWidth: 320 },
      timeRow: { gridTemplateColumns: '1fr 1fr 1fr' },
    },
  },
});

export const StackVertical = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  variants: {
    gap: {
      sm: { gap: 6 },
      md: { gap: 8 },
      lg: { gap: 12 },
      xl: { gap: 16 },
    },
  },
});

export const DangerButton = styled('button', {
  background: '$colors$dangerDark',
  color: '#FFFFFF',
  border: 'none',
  padding: '6px 12px',
  borderRadius: 6,
  cursor: 'pointer',
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

export const ErrorBlock = styled('p', {
  color: '$colors$dangerDefault',
});
